export function cmpOptimizelyBridge() {
  "use strict";

  //===============
  //   Constants
  //===============

  // CookiePro "Performance/Analytics" category ID
  const ANALYTICS_CAT = "C0002";

  // Optimizely cookies to remove when Performance consent is not granted
  const OPTIMIZELY_COOKIES = ["optimizelyOptOut", "optimizelySession"];
  const OPTIMIZELY_COOKIE_DOMAIN = ".solarwinds.com";

  //===============
  //   Functions
  //===============

  // Check if user has granted analytics consent via CookiePro
  const hasAnalyticsConsent = () => {
    try {
      if (!window.OnetrustActiveGroups) {
        return false;
      }

      const groups = window.OnetrustActiveGroups;
      return groups.includes(ANALYTICS_CAT);
    } catch {
      return false;
    }
  };

  // Hold Optimizely events until consent is determined
  const holdEvents = () => {
    try {
      window.optimizely?.push({ type: "holdEvents" });
    } catch (error) {
      console.error("[Optimizely] Failed to hold events:", error);
    }
  };

  const removeOptimizelyCookies = () => {
    try {
      const present = OPTIMIZELY_COOKIES.filter((name) =>
        document.cookie.split("; ").some((c) => c.startsWith(`${name}=`)),
      );

      // Also expire the host-only variant (no domain attribute)
      OPTIMIZELY_COOKIES.forEach((name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${OPTIMIZELY_COOKIE_DOMAIN}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });

      const remaining = OPTIMIZELY_COOKIES.filter((name) =>
        document.cookie.split("; ").some((c) => c.startsWith(`${name}=`)),
      );

      if (remaining.length) {
        console.warn(
          `[Optimizely] No Performance consent - could not remove cookies: ${remaining.join(", ")}`,
        );
      } else {
        console.info(
          present.length
            ? `[Optimizely] No Performance consent - removed cookies: ${present.join(", ")}`
            : "[Optimizely] No Performance consent - no Optimizely cookies to remove",
        );
      }
    } catch (error) {
      console.error("[Optimizely] Failed to remove cookies:", error);
    }
  };

  // Release events with appropriate consent settings
  const releaseEvents = () => {
    try {
      window.optimizely = window.optimizely || undefined;

      if (!window.optimizely) {
        throw new Error("Optimizely object not found on window.");
      }

      const hasConsent = hasAnalyticsConsent();
      window.optimizely.push({ type: "setOptOut", isOptOut: !hasConsent });

      if (hasConsent) {
        window.optimizely.push({ type: "sendEvents" });
        document.dispatchEvent(new Event("optly:consent-ready"));
        console.info("[Optimizely] Performance consent granted - events sent");
      } else {
        holdEvents();
        console.info(
          "[Optimizely] No Performance consent - opted out, events held",
        );
      }

      // Store current consent state
    } catch (error) {
      console.error("[Optimizely] Failed to release events:", error);
    }

    // Runs after setOptOut (which writes optimizelyOptOut) and also when
    // the Optimizely object is missing, so no cookies survive without consent.
    removeCookiesIfNoConsent();
  };

  const removeCookiesIfNoConsent = () => {
    if (!hasAnalyticsConsent()) {
      removeOptimizelyCookies();
    }
  };

  const waitForOneTrust = () => {
    // Check if OneTrust is already loaded
    if (window.OnetrustActiveGroups) {
      releaseEvents();
      return;
    }

    let isResolved = false;

    // Fallback: Poll for OnetrustActiveGroups (in case event doesn't fire)
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    const pollInterval = setInterval(() => {
      attempts++;

      if (window.OnetrustActiveGroups) {
        if (!isResolved) {
          isResolved = true;
          clearInterval(pollInterval);
          releaseEvents();
        }
      } else if (attempts >= maxAttempts) {
        if (!isResolved) {
          isResolved = true;
          console.warn(
            "[Optimizely] OneTrust timeout - proceeding without consent",
          );
          clearInterval(pollInterval);
          releaseEvents();
        }
      }
    }, 100);

    // Option 1: Use OneTrust's consent event (fires when consent data is ready)
    const onConsentReady = () => {
      if (!isResolved) {
        isResolved = true;
        clearInterval(pollInterval); // Stop polling when event fires
        releaseEvents();
      }
    };

    // This event fires when OneTrust is fully loaded
    window.addEventListener("OneTrustGroupsUpdated", onConsentReady, {
      once: true,
    });
  };

  //===============
  //   Execution
  //===============

  // Always listen for consent changes
  if (!window.__OPTLY_LISTENER_ATTACHED__) {
    window.addEventListener("OTConsentApplied", releaseEvents);
    window.__OPTLY_LISTENER_ATTACHED__ = true;
    // releaseEvents already removes cookies on consent change
    window.__OPTLY_CLEANUP_LISTENER_ATTACHED__ = true;
  } else if (!window.__OPTLY_CLEANUP_LISTENER_ATTACHED__) {
    // The Optimizely project JS has its own consent bridge that may claim
    // __OPTLY_LISTENER_ATTACHED__ first; it doesn't remove cookies.
    window.addEventListener("OTConsentApplied", removeCookiesIfNoConsent);
    window.__OPTLY_CLEANUP_LISTENER_ATTACHED__ = true;
  }

  waitForOneTrust();
}
